import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { ALERT_FAKE_LINK } from 'components/alerts/actions'
import Btn from 'components/buttons/Btn'
import { FooterMsg, FooterVersion } from 'components/FooterMsg'
import { BtnDesc, CollapsibleAlert, FormCollapse, FormTip, NumberSpinner, RadioGroup } from 'components/form'
import I from 'components/I'
import Screen from 'components/layout/Screen'
import DeliverySettings from 'pages/Settings/DeliverySettings'
import OtherSettings from 'pages/Settings/OtherSettings'
import { useRef, useState } from 'react'
import { Card, CardColumns, Form, InputGroup, ListGroup } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { useAccount } from 'state/Account'
import Order from 'state/models/Order'
import TabletOptions from 'state/models/TabletOptions'
import { useEnabledPrinters } from 'state/Printers'
import { isPushAllowed, isPushReceivable, pushOff, togglePush } from 'state/Push'
import { getHasReseller, getIsCMO } from 'state/Reseller'
import { getIsSingleEnabledRestaurant, useActiveRestaurant } from 'state/Restaurants'
import { logFeatures, saveSettings, useSettings } from 'state/Settings'
import { klass, newDate, MS, notifyOrder, platform, storeRefs, useEffectIf, useFieldState } from 'utils'
import PrinterSettings from './PrinterSettings'
import './styles.scss'

const sound = new Audio() //so it's not recreated upon renders

//FIXME: please, split all those boxes into different components! this is getting way too huge
//FIXME: re-runs printer search with every save call
//TODO: add some sort of anchor to make a specific box glow (to be used in help links)
export default function SettingsPage() {
	//region initialization of values and fields
	const { t }           = useTranslation()
	const dispatch        = useDispatch()
	const initialSettings = useSettings()
	const account         = useAccount()
	const canReceivePush  = useSelector(isPushReceivable)
	const isCMO           = useSelector(getIsCMO)
	const hasReseller     = useSelector(getHasReseller)
	const isSingleRest    = useSelector(getIsSingleEnabledRestaurant)
	const activeRest      = useActiveRestaurant()
	const isPushForbidden = !isPushAllowed()
	const anyPrinterOn    = useEnabledPrinters().length >= 1

	const [inputs, storage]                     = storeRefs()
	const [push, , pushField]                   = useFieldState(canReceivePush, { bool: true })
	const [playSound, , playSoundField]         = useFieldState(!!initialSettings.newOrderSound, { bool: true })
	const [newOrderAlert, , newOrderAlertField] = useFieldState(initialSettings.newOrderAlert, { bool: true })
	const [futureOrderAlert, , futureField]     = useFieldState(initialSettings.futureOrderAlert, { bool: true })
	const [notifyDue, setNotifyDue]             = useFieldState(initialSettings.newOrderWhen == 0? 'anytime' : 'within')
	const [newOrderWhen, setNewOrderWhen]       = useState(initialSettings.newOrderWhen || 1)
	const [doableTime, setDoableTime]           = useState(initialSettings.doableTime)
	const [sort, setSort]                       = useFieldState(initialSettings.sort)
	const [enableCompleted, , completedField]   = useFieldState(initialSettings.enableCompleted, { bool: true })

	const caffeineFieldState = useFieldState(initialSettings.caffeine, { bool: true })
	//endregion

	let { hash } = useLocation()
	hash = hash.replace(/^#/, '')
	useEffectIf(!!hash, () => {
		const target = document.querySelector(`#card-${hash}`)
		if (target) {
			window.setTimeout(() => {
				target.scrollIntoView({ block: 'center', inline: 'center' })
				target.classList.add('cardHighlight')
				target.onanimationend = () => {
					target.classList.remove('cardHighlight')
					target.onanimationend = undefined //seppuku
				}
			}, 350)
		}
	}, [hash])

	//TODO: track if there was any change that actually needs the API - printers might not, for instance
	async function doSave() {
		await Promise.all([
			dispatch(logFeatures({ auto_print: anyPrinterOn })),
			dispatch(push? togglePush(push) : pushOff), //the pushOn API call doesn't happen here, but at App.js once the settings change
			dispatch(saveSettings({
				newOrderSound: playSound? inputs.newOrderSound.value : false,
				loopSound    : inputs.loopSound.checked,
				louderSound  : inputs.louderSound.checked,
				newOrderAlert,
				futureOrderAlert,
				flashScreen  : inputs.flashScreen.checked,
				newOrderWhen : notifyDue == 'anytime'? 0 : newOrderWhen,
				doableTime,
				sort,
				enableCompleted,
				caffeine     : caffeineFieldState[0],
			}))
		])
	}

	//region sound vars and functions
	/** @type {{ current: HTMLLinkElement }}  */
	const playBtn                    = useRef()
	const [soundPlaying, setPlaying] = useState(false)
	const [soundLooping, setLooping] = useState(0)
	// sound.volume = 0.1 //uncomment if you're testing and fed up of those sounds :))
	sound.onpause = () => setPlaying(false)
	sound.onplay  = () => setPlaying(true)
	function testSound() {
		if (!sound.paused) {
			sound.pause()
			if (soundLooping) {
				window.clearTimeout(soundLooping)
				setLooping(0)
			}
		} else {
			sound.src  = TabletOptions.soundFile(inputs.newOrderSound.value, inputs.loopSound.checked, inputs.louderSound.checked)
			sound.loop = (newOrderAlert //shouldn't loop without an alert for the user to close
				&& inputs.loopSound.checked) //still gaps, couldn't hack a fix https://stackoverflow.com/a/22446616/102960
			sound.play().then(() => {
				if (sound.loop) {
					const LOOP_DURATION = 10 * MS.SEC //longest sound (fanfare) has 6s
					playBtn.current.style.animationDuration = LOOP_DURATION+'ms'
					setLooping(window.setTimeout(() => {
						sound.pause()
						setLooping(0)
					}, LOOP_DURATION))
				} else {
					playBtn.current.style.animationDuration = sound.duration+'s'
				}
			})
		}
	}
	//endregion

	function testAlert() {
		const orderInfo = {
			ready_at     : newDate(null),
			created_at   : newDate(null),
			number       : 123,
			shouldNotify : true,
			id           : ALERT_FAKE_LINK,
			customer_name: (Math.random() >= 0.5? 'Jane' : 'John') + ' Doe',
		}
		const order = new Order(orderInfo)

		const options = {
			loopSound    : inputs.loopSound.checked,
			louderSound  : inputs.louderSound.checked,
			newOrderAlert: true,
			newOrderSound: playSoundField.checked? inputs.newOrderSound.value : playSoundField.checked,
			flashScreen  : inputs.flashScreen.checked,
		}

		notifyOrder(order, { force: true }, options)
	}

	const logoutBtn = <Btn as={Link} variant="outline-danger" icon="door-open" to="/logout">{t('Log out')}</Btn>
	return <Screen title={t('Settings')} corner={logoutBtn}>
		<Screen.Content addPadding>
			<CardColumns>
				<Card>
					<Card.Header><Icon icon="bell"/> {t('New order notifications')}</Card.Header>
					<ListGroup variant="flush">
						<ListGroup.Item>
							<Form.Group controlId="push">
								<Form.Switch label={t("Use push updates")} {...pushField} disabled={isPushForbidden}/>
								{!isPushForbidden && <FormTip>{t('Push notification volume follows your device\'s alert settings.')}</FormTip>}
								<CollapsibleAlert in={!push || isPushForbidden}>
									{isPushForbidden && <I b>{platform.isIOSSimulator()?
										'iOS simulators don\'t really like push notifications, sorry!' :
										t('We can\'t push to web browsers yet.')}{' '}
										{(platform.isWeb() && platform.isMobile() && account.app_url) && <Trans>However, you can <a href={account.app_url} target="_blank" rel="noreferrer" className="alert-link">install the native app</a> and enable push there!<br/></Trans>}
										<br/>
									</I>}
									<Trans>Your orders will refresh every minute, <b>only while the {{ pageOrApp: platform.pageOrApp(t) }} is open</b>.
										You can always use the <BtnDesc icon="sync"/> in the top-right corner of the Orders List.</Trans>
								</CollapsibleAlert>
							</Form.Group>
						</ListGroup.Item>

						<ListGroup.Item>
							<Card.Subtitle>{t('In-app notifications')}</Card.Subtitle>
							{/* FIXME: move this and all the sound code to a separate component */}
							<Form.Group>
								<Form.Switch label={t("Play sound")} id="playSound" {...playSoundField}/>

								<FormCollapse secondary in={playSound}>
									<Form.Group>
										<Form.Label htmlFor="newOrderSound">{t('Style:')}</Form.Label>
										<InputGroup>
											<Form.Control as="select" ref={storage} id="newOrderSound" defaultValue={initialSettings.newOrderSound}>
												{Object.entries(TabletOptions.SOUNDS).map(([key, name]) => (
													<option value={key} key={key}>{name}</option>
												))}
											</Form.Control>
											<InputGroup.Append>
												<Btn variant="secondary" onClick={testSound} swap icon={soundPlaying? 'stop' : 'play'} ref={playBtn} {...klass({ playing: soundPlaying })}>
													{soundPlaying? t('Stop', { context: '...a sound' }) : t('Test', { context: '...a sound' })}
												</Btn>
											</InputGroup.Append>
										</InputGroup>
									</Form.Group>

									<Form.Group>
										<Form.Switch label={t("Make it louder")} id="louderSound" ref={storage} defaultChecked={initialSettings.louderSound}/>
										<FormTip><b>{t('Caution when testing!')}</b> {t('It\'s an artificial amplification; may create small distortions on some devices.')}</FormTip>
									</Form.Group>
								</FormCollapse>
							</Form.Group>
							<Form.Group>
								<Form.Switch label={t("Show alert")} id="newOrderAlert" {...newOrderAlertField}/>

								<FormCollapse secondary in={newOrderAlert}>
									<Form.Group>
										<Form.Switch label={t("Flash screen until acknowledged")} id="flashScreen" ref={storage} defaultChecked={initialSettings.flashScreen}/>
										<FormTip>{t('More visual-appealing alert, flashing until someone interacts with the screen.')}
											<b className="d-reduced-motion">{t('You have the "Reduce Motion" system setting on - this reduces the flashing alert to a static fullscreen alert.')}</b></FormTip>
									</Form.Group>

									<Form.Group>
										<Form.Switch label={t("Repeat sound until acknowledged")} id="loopSound" ref={storage} defaultChecked={initialSettings.loopSound}/>
										<FormTip>
											{t('The sound will loop until you interact with the alert.')}{' '}
											{!isPushForbidden && <b>{t('Push notifications are not affected, only in-app alerts.')}</b>}
										</FormTip>
									</Form.Group>

									<Form.Group>
										<Btn variant="secondary" icon="play" swap onClick={testAlert}>{t('Test alert')}</Btn>
									</Form.Group>
								</FormCollapse>
							</Form.Group>
						</ListGroup.Item>

						<ListGroup.Item>
							<RadioGroup name="due" state={notifyDue} onChange={setNotifyDue} label={t("Notify for new orders due")}
							            values={{
							              anytime: t('Anytime today'),
							              // this may become <NumberSpinner/>之内 (gets swapped)
								            within: <Trans>
									            <span>Within</span>
									            <NumberSpinner value={newOrderWhen} setter={setNewOrderWhen}
								                             min={1} max={48} size="sm" appendText={[t('hour'), t('hours')]}
								                             disabled={notifyDue=='anytime'}/>
								            </Trans>
							            }}
													tip={anyPrinterOn && t('This affects which orders would auto-print in Star Micronics printers.')}/> {/* TODO: replace this permanent tip with FormCollapse once we get the margin/padding flash bug fixed */}

							<Form.Group>
								<Form.Switch label={t('Notify for Future Orders')} id="futureOrderAlert" {...futureField}/>
								<FormTip>
									<Trans>Any order due from tomorrow on is considered to be a Future Order, and will show up in the <BtnDesc {...Order.ICONS[Order.STATUS.FUTURE]} noText={true}/> "Future" screen.</Trans>
									{!hasReseller && <>
										<br/>{t('For this feature to work, your locations must be set to have orders sent immediately;')}{' '}
					          {isSingleRest && activeRest? <Trans>go to <a href={activeRest.adminUrl()}>the Admin</a>, or contact us.</Trans> :
											t("go to the location's General Settings in the Admin, or contact us.") /* TODO in some twisted scenario, some single-loc accounts would have no activeRest; this was already patched on 2.21.2 and will be slowly fixed by automatic API calls and people getting updated; in the near future, add a check with isSingleRest&&activeRest to report this to Sentry and, if there are not reports in a month or so, remove the `activeRest` from this check */}
									</>}
								</FormTip>
							</Form.Group>
						</ListGroup.Item>
					</ListGroup>
				</Card>

				<Card>
					<Card.Header><Icon icon="list"/> {t('Orders List')}</Card.Header>
					<ListGroup variant="flush">
						<ListGroup.Item>
							<Form.Group controlId="doableTime">
								<Form.Label>{t('Highlight orders due in:')}</Form.Label>
								<NumberSpinner value={doableTime} setter={setDoableTime} min={0} step={5} appendText={t("minutes")}/>
								<FormTip><Trans>Their time will be shown as red in the list. <span className="text-nowrap">Use <tt>0</tt> to disable</span>.</Trans></FormTip>
							</Form.Group>
						</ListGroup.Item>

						<ListGroup.Item>
							<RadioGroup name="sort" state={sort} onChange={setSort} label={t("Show first in the list")} values={{
								due: t('Earliest due times'),
								created: t('Newest orders')
							}}/>
						</ListGroup.Item>

						<ListGroup.Item>
							<Form.Group>
								<Form.Switch label={t('Enable "Mark as Completed"')} id="enableCompleted" {...completedField}/>
								<FormTip>
									{t('Past due orders behave differently according to this setting:')}
									<ul>
										<li {...klass({ itemOff: !enableCompleted })}>{t('On: receive extra highlight in the list;')}</li>
										<li {...klass({ itemOff: enableCompleted })}>{t('Off: move to Completed after 1 hour.')}</li>
									</ul>
								</FormTip>
							</Form.Group>
						</ListGroup.Item>
					</ListGroup>
				</Card>

				{isCMO && <DeliverySettings/>}

				<PrinterSettings/>
				<OtherSettings caffeineFieldState={caffeineFieldState}/>
			</CardColumns>

			{/* TODO: maybe add here a tiny refresh button, that taps into a custom reload() function from Cordova's www/index.html that refreshes the index file itself (and hopefully, re-fetching an updated payload); if that doesn't exist, we simply run a window.refresh? should probably be replaced by a force-refresh of the SW contents */}

			<FooterVersion/>

			<FooterMsg><Link to="/terms">{t('Terms of Service')}</Link></FooterMsg>

			{/* Little Robot sounds: 8bit victory, harp tones */}
			{playSound && <FooterMsg>
				{t('Some sounds by')}{' '}
				<a href="https://www.zapsplat.com/author/little-robot-sound-factory/" title={t('Creative Commons - Attribution License')}>Little Robot - CC-BY {t('License')}</a>{/* eslint-disable-line i18next/no-literal-string */}
			</FooterMsg>}

			{/*platform.isDev() && (
				<details><summary>Show restaurant color groups</summary>
					<table className="table table-bordered table-sm" style={{fontSize:'80%'}}>
						{range(2, 14).map(count => (
							range(0, Math.ceil(10/count)).map(seed => (
								<tr>
									<th>{count}&nbsp;rest&nbsp;#{seed+1}</th>
									{DistinguishableColors.selectGroup(count, seed).map(c => <td style={{backgroundColor: c, color: 'white'}}>{c}</td>)}
								</tr>
							))
						))}
					</table>
				</details>
			)*/}
		</Screen.Content>

		<Screen.SaveFooter action={doSave}/>

	</Screen>
}
